import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Box,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import Swal from 'sweetalert2';
import memberAPI from '../../middleware/memberAPI';
import { alertError } from '../../Util/Util';
import { trim } from 'lodash';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: '550px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  infoSection: {
    marginBottom: theme.spacing(3),
  },
  memberHeader: {
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  memberName: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  planName: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  statusLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  statusValue: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  targetSection: {
    marginTop: theme.spacing(3),
  },
  lookupRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  targetInfoBox: {
    marginTop: theme.spacing(2),
  },
  targetInfoItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#e3f2fd',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const UserPayingMemberTransferModal = ({
  open,
  userPayingMember,
  onClose,
  onSuccess,
}) => {
  const classes = useStyles();
  const [targetUserId, setTargetUserId] = useState('');
  const [targetUserInfo, setTargetUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  useEffect(() => {
    if (open) {
      setTargetUserId('');
      setTargetUserInfo(null);
      setIsLoading(false);
      setLookupError(null);
    }
  }, [open]);

  if (!userPayingMember) {
    return null;
  }

  const handleLookupUser = () => {
    const userId = trim(targetUserId);

    if (userId === '') {
      return;
    }

    if (userId === String(userPayingMember.userInfo.id)) {
      setLookupError('不能轉讓給自己');
      setTargetUserInfo(null);
      return;
    }

    setIsLoading(true);
    setLookupError(null);
    setTargetUserInfo(null);

    memberAPI.getMember(userId).then(
      (rs) => {
        if (!rs) {
          setLookupError('查無此會員，請確認 UserId 是否正確');
        } else {
          setTargetUserInfo(rs);
        }
        setIsLoading(false);
      },
      (error) => {
        setLookupError('查詢失敗，請確認 UserId 是否正確');
        setIsLoading(false);
      },
    );
  };

  const handleTransfer = () => {
    Swal.fire({
      title: '確認轉讓',
      text: `確定要將此VIP會員記錄轉讓給 ${targetUserInfo.realName} 嗎？轉讓後無法還原操作！`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '確定轉讓',
      confirmButtonColor: '#d33',
      cancelButtonText: '取消',
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);

        memberAPI
          .transferPayingMember(userPayingMember.id, trim(targetUserId))
          .then(
            () => {
              Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                title: '轉讓成功',
                icon: 'success',
              });
              if (onSuccess) {
                onSuccess();
              }
              onClose();
            },
            (error) => {
              alertError(error);
            },
          )
          .then(() => {
            setIsLoading(false);
          });
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      disableBackdropClick
      disableEscapeKeyDown>
      <DialogTitle>轉讓VIP會員</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Alert severity="info" style={{ marginBottom: '16px' }}>
          轉讓僅限會員資格，方案所含套券內容不會一起轉讓。
        </Alert>

        {/* 來源會員資訊 */}
        <Box className={classes.infoSection}>
          <Box className={classes.memberHeader}>
            <div className={classes.memberName}>
              {userPayingMember.userInfo.realName}
            </div>
            <div className={classes.planName}>
              {userPayingMember.payingMemberInfo
                ? userPayingMember.payingMemberInfo.name
                : '無方案'}
              {` - ID: ${userPayingMember.id}`}
            </div>
          </Box>

          <Box>
            <Box className={classes.statusItem}>
              <span className={classes.statusLabel}>會員ID</span>
              <span className={classes.statusValue}>
                {userPayingMember.userInfo.id}
              </span>
            </Box>
            <Box className={classes.statusItem}>
              <span className={classes.statusLabel}>方案</span>
              <span className={classes.statusValue}>
                {userPayingMember.payingMemberInfo
                  ? userPayingMember.payingMemberInfo.name
                  : '無'}
              </span>
            </Box>
            <Box className={classes.statusItem}>
              <span className={classes.statusLabel}>到期日</span>
              <span className={classes.statusValue}>
                {moment(userPayingMember.endAt).format('YYYY-MM-DD')}
              </span>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* 目標會員輸入區 */}
        <Box className={classes.targetSection}>
          <div className={classes.lookupRow}>
            <TextField
              label="目標會員 UserId"
              variant="outlined"
              size="small"
              value={targetUserId}
              onChange={(e) => {
                setTargetUserId(e.target.value);
                setTargetUserInfo(null);
                setLookupError(null);
              }}
              disabled={isLoading}
              style={{ flex: 1 }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleLookupUser();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleLookupUser}
              disabled={isLoading || trim(targetUserId) === ''}
              size="small">
              查詢會員
            </Button>
          </div>

          {lookupError && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {lookupError}
            </Alert>
          )}

          {targetUserInfo && (
            <Box className={classes.targetInfoBox}>
              <Alert severity="info" style={{ marginBottom: '12px' }}>
                已找到目標會員，請確認以下資訊
              </Alert>

              <Box className={classes.targetInfoItem}>
                <span className={classes.statusLabel}>姓名</span>
                <span className={classes.statusValue}>
                  {targetUserInfo.realName}
                </span>
              </Box>
              <Box className={classes.targetInfoItem}>
                <span className={classes.statusLabel}>電話</span>
                <span className={classes.statusValue}>
                  {targetUserInfo.cellPhone || '未填寫'}
                </span>
              </Box>
              <Box className={classes.targetInfoItem}>
                <span className={classes.statusLabel}>Email</span>
                <span className={classes.statusValue}>
                  {targetUserInfo.email || '未填寫'}
                </span>
              </Box>

              <Alert severity="warning" style={{ marginTop: '16px' }}>
                轉讓後無法還原操作，請確認目標會員資訊正確無誤。
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading} color="default">
          關閉
        </Button>
        {targetUserInfo && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleTransfer}
            disabled={isLoading}>
            確認轉讓
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

UserPayingMemberTransferModal.propTypes = {
  open: PropTypes.bool.isRequired,
  userPayingMember: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

UserPayingMemberTransferModal.defaultProps = {
  userPayingMember: null,
  onSuccess: null,
};

export default UserPayingMemberTransferModal;
